// API Document 전용 스타일

export const colors = {
  // HTTP 메서드 배지 색상
  methodColors: {
    POST: '#49cc90',
    GET: '#61affe',
    PUT: '#fca130',
    DELETE: '#f93e3e',
  },
  // 배경 색상
  background: {
    sidebar: '#ffffff',
    content: '#ffffff',
    toc: '#fafafa',
    page: '#f8fafc',
  },
  // 코드 블록 색상
  code: {
    background: '#1e1e1e',
    text: '#d4d4d4',
  },
  // 텍스트 색상
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    muted: '#9ca3af',
  },
  // 보더 색상
  border: {
    light: '#e5e7eb',
    medium: '#d1d5db',
  },
  // 액센트 색상
  accent: {
    primary: '#1A62EA',
    primaryHover: '#1550c7',
  },
};

export const apiDocStyles = {
  // 전체 페이지 컨테이너
  pageContainer: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: colors.background.page,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  // 사이드바
  sidebar: {
    width: '340px',
    minWidth: '340px',
    backgroundColor: colors.background.sidebar,
    borderRight: `1px solid ${colors.border.light}`,
    height: '100vh',
    position: 'sticky',
    top: 0,
    overflowY: 'auto',
    padding: '24px 0',
  },

  sidebarHeader: {
    padding: '0 20px 20px',
    borderBottom: `1px solid ${colors.border.light}`,
    marginBottom: '16px',
  },

  sidebarLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    color: colors.text.primary,
    fontWeight: '700',
    fontSize: '18px',
  },

  sidebarCategory: {
    padding: '12px 20px',
    fontSize: '18px',
    fontWeight: '600',
    color: colors.text.primary,
    letterSpacing: '0.5px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  sidebarMenuItem: (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px 12px 32px',
    fontSize: '15px',
    color: isActive ? colors.accent.primary : colors.text.secondary,
    backgroundColor: isActive ? '#EEF4FF' : 'transparent',
    borderRight: isActive ? `3px solid ${colors.accent.primary}` : 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.15s ease',
  }),

  // 메인 컨텐츠
  mainContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },

  contentWrapper: {
    flex: 1,
    padding: '32px 48px',
  },

  // 브레드크럼
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    color: colors.text.secondary,
  },

  breadcrumbLink: {
    color: colors.text.secondary,
    textDecoration: 'none',
    cursor: 'pointer',
  },

  breadcrumbSeparator: {
    color: colors.text.muted,
  },

  breadcrumbCurrent: {
    color: colors.text.primary,
    fontWeight: '500',
  },

  // 페이지 제목
  pageTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: '8px',
    lineHeight: '1.3',
  },

  pageDescription: {
    fontSize: '16px',
    color: colors.text.secondary,
    marginBottom: '32px',
    lineHeight: '1.6',
  },

  // HTTP 메서드 배지
  methodBadge: (method) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#fff',
    backgroundColor: colors.methodColors[method] || colors.methodColors.GET,
    textTransform: 'uppercase',
  }),

  // 엔드포인트 URL
  endpointUrl: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f1f5f9',
    borderRadius: '8px',
    marginBottom: '32px',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
  },

  urlPath: {
    fontSize: '15px',
    color: colors.text.primary,
    fontWeight: '500',
  },

  // 섹션
  section: {
    marginBottom: '40px',
  },

  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${colors.border.light}`,
  },

  sectionContent: {
    fontSize: '15px',
    color: colors.text.secondary,
    lineHeight: '1.7',
  },

  // 필드 테이블
  fieldTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '24px',
    fontSize: '14px',
  },

  tableHeader: {
    textAlign: 'left',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderBottom: `2px solid ${colors.border.light}`,
    fontWeight: '600',
    color: colors.text.primary,
  },

  tableCell: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.border.light}`,
    color: colors.text.secondary,
    verticalAlign: 'top',
  },

  fieldName: {
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '13px',
    color: '#e11d48',
    fontWeight: '500',
  },

  fieldType: {
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '12px',
    color: '#7c3aed',
    backgroundColor: '#f3f4f6',
    padding: '2px 6px',
    borderRadius: '4px',
  },

  requiredBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: '600',
    color: '#dc2626',
    marginLeft: '6px',
  },

  optionalBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: '600',
    color: colors.text.muted,
    marginLeft: '6px',
  },

  // 코드 블록
  codeBlock: {
    backgroundColor: colors.code.background,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    overflow: 'auto',
  },

  codeBlockHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: '1px solid #374151',
  },

  codeBlockTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#9ca3af',
  },

  copyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: '1px solid #4b5563',
    borderRadius: '4px',
    color: '#9ca3af',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },

  codeContent: {
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '13px',
    lineHeight: '1.6',
    color: colors.code.text,
    whiteSpace: 'pre',
    margin: 0,
  },

  // TOC (목차)
  toc: {
    width: '200px',
    minWidth: '200px',
    padding: '32px 20px',
    borderLeft: `1px solid ${colors.border.light}`,
    height: 'calc(100vh - 64px)',
    position: 'sticky',
    top: '32px',
    overflowY: 'auto',
  },

  tocTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '16px',
    textAlign: 'center',
  },

  tocItem: (isActive) => ({
    display: 'block',
    padding: '6px 0',
    fontSize: isActive ? '24px' : '20px',
    fontWeight: isActive ? '600' : '400',
    color: isActive ? colors.accent.primary : colors.text.secondary,
    textDecoration: 'none',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.15s ease',
  }),

  // 엔드포인트 카드
  endpointCard: {
    backgroundColor: colors.background.content,
    border: `1px solid ${colors.border.light}`,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    transition: 'box-shadow 0.2s ease',
  },

  endpointCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },

  endpointCardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: colors.text.primary,
  },

  endpointCardDescription: {
    fontSize: '14px',
    color: colors.text.secondary,
    lineHeight: '1.5',
  },

  // 탭
  tabContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
    borderBottom: `1px solid ${colors.border.light}`,
  },

  tab: (isActive) => ({
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: isActive ? colors.accent.primary : colors.text.secondary,
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: isActive ? `2px solid ${colors.accent.primary}` : '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  }),

  // 백 버튼
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    color: colors.text.secondary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.border.medium}`,
    borderRadius: '6px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.15s ease',
    marginBottom: '24px',
  },

  // 모바일 스타일
  mobileMenuButton: {
    display: 'none',
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: colors.accent.primary,
    color: '#fff',
    border: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
};

// 반응형 스타일 조정
export const getResponsiveStyles = (isMobile) => ({
  pageContainer: {
    ...apiDocStyles.pageContainer,
    flexDirection: isMobile ? 'column' : 'row',
  },
  sidebar: {
    ...apiDocStyles.sidebar,
    display: isMobile ? 'none' : 'block',
    position: isMobile ? 'fixed' : 'sticky',
    zIndex: isMobile ? 1001 : 'auto',
  },
  contentWrapper: {
    ...apiDocStyles.contentWrapper,
    padding: isMobile ? '20px 16px' : '32px 48px',
  },
  toc: {
    ...apiDocStyles.toc,
    display: isMobile ? 'none' : 'block',
  },
  mobileMenuButton: {
    ...apiDocStyles.mobileMenuButton,
    display: isMobile ? 'flex' : 'none',
  },
});

export default apiDocStyles;
